import { useState } from "react";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getIcsInfo, revokeIcsToken, rotateIcsToken } from "@/api/ics";

export function IcsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);

  const info = useQuery({
    queryKey: ["me-ics"],
    queryFn: () => getIcsInfo(),
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotateIcsToken(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me-ics"] }),
  });
  const revokeMutation = useMutation({
    mutationFn: () => revokeIcsToken(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me-ics"] }),
  });

  const handleCopy = async () => {
    if (!info.data?.url) return;
    try {
      await navigator.clipboard.writeText(info.data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EventRepeatIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("profile.ics.title")}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("profile.ics.intro")}
        </Typography>

        {info.isLoading && <CircularProgress size={24} />}

        {info.data && !info.data.enabled && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("profile.ics.notEnabled")}
            </Typography>
            <Box>
              <Button
                variant="contained"
                onClick={() => rotateMutation.mutate()}
                disabled={rotateMutation.isPending}
              >
                {t("profile.ics.generate")}
              </Button>
            </Box>
          </Stack>
        )}

        {info.data && info.data.enabled && info.data.url && (
          <Stack spacing={2}>
            <Alert severity="warning" variant="outlined">
              {t("profile.ics.warning")}
            </Alert>
            <TextField
              label={t("profile.ics.url")}
              value={info.data.url}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title={copied ? t("profile.ics.copied") : t("profile.ics.copy")}>
                    <IconButton size="small" onClick={handleCopy}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: 13 } }}
            />
            {info.data.unavailabilities_url && (
              <TextField
                label={t("profile.ics.unavailUrl")}
                value={info.data.unavailabilities_url}
                fullWidth
                size="small"
                helperText={t("profile.ics.unavailHelp")}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title={t("profile.ics.copy")}>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (info.data?.unavailabilities_url) {
                            try {
                              await navigator.clipboard.writeText(
                                info.data.unavailabilities_url,
                              );
                            } catch {
                              // clipboard restricted — silent
                            }
                          }
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: 13 } }}
              />
            )}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<AutorenewIcon />}
                onClick={() => {
                  if (confirm(t("profile.ics.rotateConfirm"))) rotateMutation.mutate();
                }}
                disabled={rotateMutation.isPending}
              >
                {t("profile.ics.rotate")}
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  if (confirm(t("profile.ics.revokeConfirm"))) revokeMutation.mutate();
                }}
                disabled={revokeMutation.isPending}
              >
                {t("profile.ics.revoke")}
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
